import React from 'react';
import { useCategoryPrintResults } from '../../hooks/useCategoriesData';
import { supabase } from '../../utils/supabaseClient';
import type { CategoryPrintRow, CategorySearchFilters } from '../../types/rpc-contracts';

interface ErrorWithMessage {
  message: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && 'message' in error;
}

function getErrorMessage(error: unknown): string {
  if (isErrorWithMessage(error)) {
    return error.message;
  }
  return 'An unknown error occurred';
}

interface PrintJobMeta {
  created_at: string;
  filters: CategorySearchFilters | Record<string, any> | null;
  selected_ids?: string[] | null;
}

function getJobIdFromHash(): string | null {
  const hash = window.location.hash || '';
  const query = hash.split('?')[1] || '';
  const params = new URLSearchParams(query);
  return params.get('job_id');
}

function formatFilterSummary(filters: CategorySearchFilters | Record<string, any> | null): string {
  if (!filters) return 'All categories';

  const summary: string[] = [];
  const typedFilters = filters as CategorySearchFilters;

  if (typedFilters.text_search) summary.push(`Search: "${typedFilters.text_search}"`);

  if (typedFilters.has_bisac === true) summary.push('Has BISAC mapping');
  if (typedFilters.has_bisac === false) summary.push('No BISAC mapping');

  if (Array.isArray(typedFilters.deadline_structure) && typedFilters.deadline_structure.length) {
    summary.push(`Deadline: ${typedFilters.deadline_structure.join(', ')}`);
  }

  if (typeof typedFilters.entry_fee_min === 'number') {
    summary.push(`Entry fee ≥ $${typedFilters.entry_fee_min}`);
  }

  if (typeof typedFilters.entry_fee_max === 'number') {
    summary.push(`Entry fee ≤ $${typedFilters.entry_fee_max}`);
  }

  if (typedFilters.sort_by) {
    summary.push(`Sort: ${typedFilters.sort_by} ${typedFilters.sort_direction ?? 'asc'}`);
  }

  return summary.length ? summary.join(' • ') : 'All categories';
}

const CategoryPrintResultsPage: React.FC = () => {
  const jobId = React.useMemo(() => getJobIdFromHash(), []);
  const { data: rows = [], isLoading, isError, error } = useCategoryPrintResults(jobId);
  const [jobMeta, setJobMeta] = React.useState<PrintJobMeta | null>(null);
  const [metaError, setMetaError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    async function fetchMeta() {
      if (!jobId) {
        setMetaError('Missing job_id in URL');
        return;
      }

      try {
        const { data, error: metaErr } = await supabase
          .from('print_jobs_cat')
          .select('created_at, filters, selected_ids')
          .eq('id', jobId)
          .maybeSingle();

        if (metaErr) throw metaErr;

        if (isMounted) {
          if (!data) {
            setJobMeta(null);
            setMetaError('Print job not found or expired.');
          } else {
            setJobMeta(data as unknown as PrintJobMeta);
            setMetaError(null);
          }
        }
      } catch (err: unknown) {
        if (isMounted) {
          setMetaError(getErrorMessage(err) || 'Failed to load print job metadata');
        }
      }
    }

    void fetchMeta();

    return () => {
      isMounted = false;
    };
  }, [jobId]);

  const selectedCount = jobMeta?.selected_ids ? jobMeta.selected_ids.length : 0;
  const filtersSummary = formatFilterSummary(jobMeta?.filters ?? null);

  return (
    <div style={{ background: '#fff', color: '#111827' }}>
      <style>{`
        @media print {
          @page {
            margin: 12mm;
            size: landscape;
          }
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; }
        }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; }
        thead th { background: #f3f4f6; text-align: left; font-weight: 600; }
      `}</style>

      <div style={{ padding: '24px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px' }}>Category Search Results</h1>
            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
              {jobMeta?.created_at ? `Generated ${new Date(jobMeta.created_at).toLocaleString()}` : metaError || 'Preparing job details…'}
            </div>
            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
              {selectedCount > 0 ? `Selected categories: ${selectedCount}` : `Filters: ${filtersSummary}`}
            </div>
            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
              Total rows: {rows.length.toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            className="no-print"
            onClick={() => window.print()}
            style={{
              padding: '0.6rem 1.2rem',
              borderRadius: '999px',
              border: '1px solid #111827',
              background: '#fde047',
              color: '#111827',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Print
          </button>
        </header>

        {!jobId && (
          <div style={{ color: '#b91c1c', marginBottom: '1rem' }}>
            Missing job id. Please close this tab and generate the print job again.
          </div>
        )}

        {isError && !isLoading && (
          <div style={{ color: '#b91c1c', marginBottom: '1rem' }}>
            {getErrorMessage(error) || 'Failed to load print results.'}
          </div>
        )}

        {!isError && !isLoading && rows.length === 0 && (
          <div style={{ color: '#6b7280', marginBottom: '1rem' }}>
            No categories found for this print job.
          </div>
        )}

        {isLoading && (
          <div style={{ color: '#6b7280', marginBottom: '1rem' }}>Preparing category results…</div>
        )}

        {!isLoading && rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th style={{ width: '4%' }}>#</th>
                <th style={{ width: '12%' }}>Program</th>
                <th style={{ width: '11%' }}>Program code</th>
                <th style={{ width: '18%' }}>Category</th>
                <th style={{ width: '15%' }}>Tier hierarchy</th>
                <th style={{ width: '10%' }}>Entry fee</th>
                <th style={{ width: '12%' }}>Submission closes</th>
                <th style={{ width: '8%' }}>BISAC mapped</th>
                <th style={{ width: '10%' }}>Mappings count</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: CategoryPrintRow, idx: number) => (
                <tr key={`${row.category_id}-${idx}`}>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{idx + 1}</td>
                  <td>{row.program_name}</td>
                  <td>{row.program_code || '—'}</td>
                  <td>{row.category_name}</td>
                  <td>
                    <div>{row.tier_1 || '—'}</div>
                    <div style={{ color: '#6b7280' }}>{row.tier_2 || '—'}</div>
                    <div style={{ color: '#9ca3af' }}>{row.tier_3 || '—'}</div>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.entry_fee !== null && row.entry_fee !== undefined ? `$${Number(row.entry_fee).toFixed(2)}` : '—'}
                  </td>
                  <td>{row.submission_closes ? new Date(row.submission_closes).toLocaleDateString() : '—'}</td>
                  <td style={{ textAlign: 'center' }}>{row.has_bisac_mapping ? 'Yes' : 'No'}</td>
                  <td style={{ textAlign: 'center' }}>{row.mapping_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CategoryPrintResultsPage;
